import { useEffect, useCallback, useState } from 'react';
import { CONFIG } from '../../config';
import { NULL_USER } from '../../lib/user';
import { reportError } from '../../lib/reportError';
import {
  createBrainTreeClient,
  createPaypalCheckoutInstance,
  waitForPayPalCheckoutScripts
} from '../../lib/braintree';
import {
  loadPayPalScript,
  loadBraintreeScript,
  loadBraintreePayPalCheckoutScript
} from '../../lib/loadScript';
import { useCountry } from '../../components/CountrySelector';
import { useErrorModal } from '../useErrorModal';
import { useUser } from '../useUser';
import { usePaymentInfoEnteredTracking } from '../useCheckout';
import { CreatePayPalPaymentMethodMutationShippingAddress } from './createPayPalPaymentMethodMutation.rest';
import { useCreatePayPalPaymentMethodMutation } from './useCreatePayPalPaymentMethodMutation';

type PayPalResponse = {
  type: 'PayPalAccount';
  nonce: string;
  details: {
    countryCode: string;
    email: string;
    firstName: string;
    lastName: string;
    payerID: string;
    phone: string;
    shippingAddress: CreatePayPalPaymentMethodMutationShippingAddress;
  };
};

type PayPalButtonArgs = {
  paypalClientToken: string;
  elementSelector: string;
  height?: number | string;
  onSubmit: (response: PayPalResponse) => void;
};

const renderPaypalButton = async ({
  paypalClientToken,
  elementSelector,
  height,
  onSubmit
}: PayPalButtonArgs) => {
  const element = document.querySelector(elementSelector);

  if (!element || element.getAttribute('data-paypal-button-rendered')) return;

  // set a data attribute to avoid the button being rendered twice in the same container
  element.setAttribute('data-paypal-button-rendered', 'true');

  try {
    const client = await createBrainTreeClient(paypalClientToken);
    const paypalCheckoutInstance = await createPaypalCheckoutInstance(client);

    window.paypal.Button.render(
      {
        env: CONFIG.ENVIRONMENT === 'prod' ? 'production' : 'sandbox',
        commit: false,
        style: {
          shape: 'rect',
          color: 'white',
          size: 'responsive',
          height,
          width: 'auto',
          label: 'paypal',
          tagline: false
        },
        payment: () =>
          paypalCheckoutInstance.createPayment({
            flow: 'vault',
            enableShippingAddress: true,
            shippingAddressEditable: true
          }),
        onAuthorize: (data: unknown) =>
          paypalCheckoutInstance.tokenizePayment(
            data,
            (error: Error | undefined, payload: PayPalResponse) => {
              if (error) {
                reportError(new Error(`Error processing PayPal payment method: ${error}`));
                return;
              }
              onSubmit(payload);
            }
          )
      },
      elementSelector
    );
  } catch (error) {
    reportError(error);
  }
};

type UsePayPalButtonArgs = {
  paypalClientToken: string | null;
  elementSelector: string;
  height?: number | string;
  onSubmit?: () => Promise<void>;
};

export const usePayPalButton = ({
  paypalClientToken,
  elementSelector,
  height,
  onSubmit
}: UsePayPalButtonArgs) => {
  const { showErrorModal } = useErrorModal();
  const {
    country: { currencyCode }
  } = useCountry();
  const { setUserData } = useUser();
  const [createPaymentMethod] = useCreatePayPalPaymentMethodMutation();
  const { trackPaymentInfoEntered } = usePaymentInfoEnteredTracking();
  const [loading, setLoading] = useState(false);

  const handleSubmit = useCallback(
    async (data: PayPalResponse) => {
      setLoading(true);
      try {
        const result = await createPaymentMethod({
          variables: {
            input: {
              data: {
                nonce: data.nonce,
                email: data.details.email,
                first_name: data.details.firstName,
                last_name: data.details.lastName,
                payerID: data.details.payerID,
                shipping_address: data.details.shippingAddress,
                currency: currencyCode
              }
            }
          }
        });

        if (!result.data?.createPayPalPaymentMethod.success) {
          throw new Error('Failed to create the PayPal payment method.');
        }

        if (result.data.createPayPalPaymentMethod.guestAccessToken) {
          setUserData({
            ...NULL_USER,
            accessToken: result.data.createPayPalPaymentMethod.guestAccessToken,
            email: data.details.email,
            isMember: result.data.createPayPalPaymentMethod.isMember,
            remember: true
          });
        }

        await onSubmit?.();
        trackPaymentInfoEntered();
      } catch (error) {
        reportError(error);
        showErrorModal();
      }
      setLoading(false);
    },
    [
      createPaymentMethod,
      showErrorModal,
      currencyCode,
      setUserData,
      onSubmit,
      trackPaymentInfoEntered
    ]
  );

  useEffect(() => {
    loadPayPalScript();
    loadBraintreeScript();
    loadBraintreePayPalCheckoutScript();

    const renderButton = async () => {
      if (!paypalClientToken) return;
      await waitForPayPalCheckoutScripts();
      renderPaypalButton({
        paypalClientToken,
        elementSelector,
        height,
        onSubmit: handleSubmit
      });
    };

    renderButton();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { loading };
};
